/*
  Dainton motion layer. Print coming alive: rules draw, ink fills, cards deal.

  Rules: CSS and the Web Animations API only; transform, opacity and clip-path
  only; one easing; everything plays once and never shifts layout.
  The <head> script adds .d-motion to <html> only when reduced motion is not
  requested and the page is not cart, checkout or account. Without that class
  this file does nothing and every element sits in its final state.
*/
(function () {
  'use strict';

  var html = document.documentElement;
  if (!html.classList.contains('d-motion')) return;

  var EASE = 'cubic-bezier(.2,.7,.2,1)';
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  function motionOn() {
    return html.classList.contains('d-motion') && !reduced.matches;
  }

  // If the visitor switches reduced motion on mid-visit, stop everything and
  // leave every element in its final state. CSS animations stop with the
  // class; the ones started here are finished (they all have an end).
  var running = [];
  function stopAll() {
    html.classList.remove('d-motion');
    running.forEach(function (a) { try { a.finish(); } catch (e) { a.cancel(); } });
    running = [];
  }
  if (reduced.addEventListener) reduced.addEventListener('change', function () { if (reduced.matches) stopAll(); });

  // One call for every animation: the house easing, held at the first
  // keyframe through any delay, nothing left behind once it has finished.
  function play(el, keyframes, opts) {
    var o = { duration: 500, easing: EASE, fill: 'backwards' };
    for (var k in opts) o[k] = opts[k];
    try {
      var a = el.animate(keyframes, o);
      running.push(a);
      a.finished.then(function () { running.splice(running.indexOf(a), 1); }, function () {});
      return a;
    } catch (e) {
      return null;
    }
  }

  // ------------------------------------------------------------ header
  // The rule and nav intro is CSS (it starts with first paint, not with this
  // file). Once the last link has risen it is marked done, so the intro never
  // replays when Alchemy re-shows the inline nav after a resize.
  var header = document.querySelector('.section-header');
  if (header) {
    header.addEventListener('animationend', function (e) {
      if (e.animationName === 'd-fade-up' && !e.target.nextElementSibling) header.classList.add('d-intro-done');
    });
  }

  // ----------------------------------------------------------- reveals
  // Each target is hidden by CSS (.d-motion X:not(.d-in)) until it is 15%
  // visible; then it gets .d-in and its animation, once. Targets that arrive
  // later (filtered product grids, theme editor reloads) are picked up by the
  // MutationObserver, so nothing is ever left hidden.
  var REVEALS = [];
  function reveal(key, selector, run) {
    REVEALS.push({ key: key, selector: selector, run: run });
  }
  // Inverted boxes play their contents themselves, in order.
  var INK_BOX = '.d-taphouse-card, .d-menu-box--inverted';

  function docOrder(a, b) {
    return a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
  }

  var io = new IntersectionObserver(function (entries) {
    var hits = [];
    entries.forEach(function (e) {
      if (e.isIntersecting) {
        io.unobserve(e.target);
        hits.push(e.target);
      }
    });
    hits.sort(docOrder);
    // Stagger counts per kind within one batch: a row of cards arriving
    // together is dealt 0, 1, 2, 3.
    var counts = {};
    hits.forEach(function (el) {
      var r = el.__dReveal;
      var n = counts[r.key] || 0;
      counts[r.key] = n + 1;
      if (!motionOn()) { el.classList.add('d-in'); return; }
      r.run(el, n);
    });
  }, { threshold: 0.15 });

  function scan(root) {
    REVEALS.forEach(function (r) {
      var list = [].slice.call(root.querySelectorAll(r.selector));
      if (root.matches && root.matches(r.selector)) list.unshift(root);
      list.forEach(function (el) {
        if (el.__dReveal || el.classList.contains('d-in')) return;
        if (r.key !== 'ink' && el.parentElement && el.parentElement.closest(INK_BOX)) return;
        el.__dReveal = r;
        io.observe(el);
      });
    });
  }

  // ------------------------------------------------------ checker band
  // The ink squares scale in along a diagonal: each one waits 20ms per
  // column (and per row) from the left edge, then takes 500ms. Squares past
  // the band's right edge are clipped anyway and are not animated.
  reveal('checker', '.d-checker', function (band) {
    band.classList.add('d-in');
    var first = band.querySelector('.d-checker__sq');
    if (!first) return;
    var size = first.offsetWidth || 48;
    var width = band.clientWidth;
    var rows = band.children;
    for (var r = 0; r < rows.length; r++) {
      var squares = rows[r].children;
      var offset = r % 2 === 0 ? 1 : 0; // odd rows start one square in
      for (var i = 0; i < squares.length; i++) {
        var col = i * 2 + offset;
        if (col * size > width) break;
        play(squares[i], [{ transform: 'scale(0)' }, { transform: 'scale(1)' }], { duration: 500, delay: (col + r) * 20 });
      }
    }
  });

  // ---------------------------------------------------------- headings
  // The heading rises out of its rule while the rule draws in from the left
  // (CSS keyframes, so the heading and its ::after rule share one clock).
  var HEADINGS = '.d-heading, .d-section-head, .d-page-head__title, .d-menu-box__heading, .banner:not(.banner--bg-image) .banner__text h1';
  function riseHeading(el, delay) {
    el.style.setProperty('--d-h', el.offsetHeight + 'px');
    el.style.setProperty('--d-delay', (delay || 0) + 'ms');
    el.classList.add('d-in');
  }
  reveal('heading', HEADINGS, function (el) { riseHeading(el, 0); });

  // ------------------------------------------- menu items, product cards
  var ITEMS = '.d-card, .d-menu-item, .d-menu-tiers, .d-menu-note, .d-award, .d-stats__item';
  function riseIn(el, delay) {
    el.classList.add('d-in');
    play(el, [
      { opacity: 0, transform: 'translateY(16px)' },
      { opacity: 1, transform: 'translateY(0)' }
    ], { duration: 500, delay: delay || 0 });
  }
  reveal('item', ITEMS, function (el, n) { riseIn(el, n * 40); });

  // -------------------------------------------------------- inverted box
  // Its border draws clockwise from the top left (one 600ms stroke round the
  // perimeter, each edge's share in proportion to its length), then ink
  // fills it from the bottom (500ms), then its contents appear: its heading
  // rises out of its rule, items rise 40ms apart, the rest rises in.
  var DRAW = 600;
  var FILL = 500;
  function inkBox(box) {
    var w = box.offsetWidth;
    var h = box.offsetHeight;
    var p = 2 * (w + h);
    var o1 = w / p;
    var o2 = (w + h) / p;
    var o3 = (2 * w + h) / p;
    var layer = document.createElement('span');
    layer.className = 'd-ink';
    layer.setAttribute('aria-hidden', 'true');
    layer.innerHTML = '<i class="d-ink__t"></i><i class="d-ink__r"></i><i class="d-ink__b"></i><i class="d-ink__l"></i><i class="d-ink__fill"></i>';
    box.appendChild(layer);
    box.classList.add('d-in', 'd-inking');

    var e = layer.children;
    function edge(el, axis, from, to) {
      var keys = [{ transform: axis + '(0)', offset: 0 }];
      if (from > 0) keys.push({ transform: axis + '(0)', offset: from });
      keys.push({ transform: axis + '(1)', offset: to });
      if (to < 1) keys.push({ transform: axis + '(1)', offset: 1 });
      play(el, keys, { duration: DRAW });
    }
    edge(e[0], 'scaleX', 0, o1);
    edge(e[1], 'scaleY', o1, o2);
    edge(e[2], 'scaleX', o2, o3);
    edge(e[3], 'scaleY', o3, 1);
    var fill = play(e[4], [{ clipPath: 'inset(100% 0 0 0)' }, { clipPath: 'inset(0 0 0 0)' }], { duration: FILL, delay: DRAW });

    var at = DRAW + FILL;
    [].forEach.call(box.children, function (child) {
      if (child === layer) return;
      if (child.matches(HEADINGS)) {
        riseHeading(child, at);
        return;
      }
      var items = child.querySelectorAll(ITEMS);
      if (child.matches(ITEMS)) items = [child];
      if (items.length) {
        child.classList.add('d-in');
        [].forEach.call(items, function (item, n) { riseIn(item, at + n * 40); });
        return;
      }
      riseIn(child, at);
    });

    function done() {
      box.classList.remove('d-inking');
      if (layer.parentNode) layer.parentNode.removeChild(layer);
    }
    if (fill) fill.finished.then(done, done);
    else done();
  }
  reveal('ink', INK_BOX, inkBox);

  // -------------------------------------------------- wordmark lockups
  // The two rules draw out from the wordmark to the edges (CSS).
  reveal('lockup', '.d-wordmark-lockup', function (el) { el.classList.add('d-in'); });

  function start() {
    scan(document);
    new MutationObserver(function (records) {
      records.forEach(function (rec) {
        rec.addedNodes.forEach(function (node) {
          if (node.nodeType === 1) scan(node);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  window.DaintonMotion = { ease: EASE, play: play, on: motionOn, reveal: reveal };
})();
